import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/hooks/useAuth'
import { getAdapter } from '@/adapters/registry'
import { accountQueryIdentifier } from '@/lib/accountQueryIdentifier'
import { getMastodonAccountId } from '@/lib/mastodon'
import type { ConnectedAccount } from '@/types/account'

interface UseAccountUnreadCountOptions {
    account: ConnectedAccount
    /** The active account's unread state is already shown by the tab/nav badge; this hook never queries it. */
    isActive: boolean
}

interface UseAccountUnreadCountResult {
    /**
     * `null` means "show no pill": the active account, an expired placeholder,
     * Nostr (excluded), or an account that has never fetched successfully.
     */
    count: number | null
    isError: boolean
}

/**
 * Per-account unread count for an inactive account-switcher row. Shares its
 * cache entry (`['unreadCount', platform, accountQueryIdentifier(...)]`) with
 * the active account's badge (`useUnreadCount`), so marking notifications
 * seen there zeroes this row for free the next time the switcher opens.
 */
export function useAccountUnreadCount({
    account,
    isActive,
}: UseAccountUnreadCountOptions): UseAccountUnreadCountResult {
    const { sessions, mastodonSessions, pixelfedSession } = useAuth()

    const blueskySession =
        account.platform === 'bluesky' ? (sessions.find(s => s.did === account.id) ?? null) : null
    const mastodonSessionForAccount =
        account.platform === 'mastodon'
            ? (mastodonSessions.find(s => getMastodonAccountId(s) === account.id) ?? null)
            : null
    // Pixelfed only ever has one connected session today, so there's nothing
    // to disambiguate by account id — this is not multi-Pixelfed-account safe (#3100).
    const pixelfedSessionForAccount =
        account.platform === 'pixelfed' ? (pixelfedSession ?? null) : null

    const session = blueskySession ?? mastodonSessionForAccount ?? pixelfedSessionForAccount

    const identifier = accountQueryIdentifier(account.platform, {
        session: blueskySession,
        mastodonSession: mastodonSessionForAccount,
        pixelfedSession: pixelfedSessionForAccount,
        nostrSession: null,
    })

    const live = account.status !== 'expired'
    const fetchEnabled = !isActive && live && session !== null

    const query = useQuery<{ count: number }>({
        queryKey: ['unreadCount', account.platform, identifier],
        enabled: fetchEnabled,
        staleTime: 30 * 1000,
        queryFn: async () => ({
            count: await getAdapter(account.platform).getUnreadCount(session),
        }),
    })

    // Gated independently of `enabled` — a disabled query can still surface a
    // cached value from before the row became inactive-ineligible.
    if (!fetchEnabled) return { count: null, isError: false }

    // `query.data`, not `query.isSuccess` — React Query flips `isSuccess` to
    // false on a failed *refetch* even though `data` still holds the last
    // successful value, and a refresh failure must leave the pill showing
    // that stale-but-known count rather than hiding it.
    return {
        count: query.data?.count ?? null,
        isError: query.isError,
    }
}

/**
 * Formats a raw unread count for the switcher-row pill: no pill for zero or
 * unknown, capped at "99+" otherwise — matching the nav-badge convention
 * (`BottomNav.component.tsx`).
 */
export function formatUnreadBadgeCount(count: number | null): string | null {
    if (count === null || count <= 0) return null
    return count > 99 ? '99+' : String(count)
}
